import {createTheme, ThemeProvider} from '@mui/material/styles';
import { makeStyles } from "@mui/styles";
import {Autocomplete, Chip, CircularProgress, FormControl, TextField, Grid} from '@mui/material';
import React, {Fragment, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import FieldNames from '../../defaults/FieldNames.json';
import DetailedFundingSources from '../../defaults/DetailedFundingSources.json';
import ErrorService from '../../services/ErrorService';
import LookupService from '../../services/LookupService';
import UserHelperService from '../../services/UserHelperService';
import DetailsPopover from '../details/customPanels/DetailsPopover';

const BlueAlien = "#1976d2";

const useStyles = makeStyles({
    underline: {
        "&&&:before": {
            borderBottom: "2px solid red"
        },
        "&&:after": {
            borderBottom: "2px solid red"
        }
    },
    condensedFormControl: {
        margin: "5px 15px 5px 15px",
        minWidth: 120,
        width: '90%'
    },
    formControl: {
        margin: 15,
        minWidth: 120,
        width: '90%'
    },
    snackBarStyle: {
        top: '120px !important'
    },
});

const theme = createTheme({
    overrides: {
        MuiPaper: {
            root: {
                backgroundColor: 'green'
            }
        },
        MuiOutlinedInput: {
            root: {
                '& $notchedOutline': {
                    //initial border
                    borderColor: BlueAlien,
                },
                '&:hover $notchedOutline': {
                    //border on hover
                    borderColor: BlueAlien,
                    border: '2px solid'
                },
                '&$focused $notchedOutline': {
                    //border on focus
                    borderColor: BlueAlien,
                    border: '2px solid'
                },
            },
        }
    },
});

export default function RecordFundingAssignment({
                                                    userInfo,
                                                    selectedRow,
                                                    setSelectedRow,
                                                    departmentSelection,
                                                    setDepartmentSelection,
                                                    agencySelection,
                                                    setAgencySelection,
                                                    regionSelection,
                                                    setRegionSelection,
                                                    unitSelection,
                                                    setUnitSelection,
                                                    subUnitSelection,
                                                    setSubUnitSelection,
                                                    tribeSelection,
                                                    setTribeSelection,
                                                    fundingUnitTypeSelection,
                                                    setFundingUnitTypeSelection,
                                                    setCostCenterCodeSelection,
                                                    setFundingUnitIDSelection,
                                                    isNew,
                                                    userRoleInfo,
                                                    rows,
                                                    isBil,
                                                    setIsBil,
                                                    requiredFields,
                                                    isTreatment,
                                                    isActual,
                                                    isPoint,
                                                    fundingValid,
                                                    setFundingValid,
                                                    isInteragencyRow
                                                }) {
    const [departmentLoading, setDepartmentLoading] = useState(false);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [agencyOpen, setAgencyOpen] = useState(false);
    const [agencyOptions, setAgencyOptions] = useState([]);
    const agencyLoading = agencyOpen && agencyOptions.length === 0;
    const [regionOpen, setRegionOpen] = useState(false);
    const [regionOptions, setRegionOptions] = useState([]);
    const regionLoading = regionOpen && regionOptions.length === 0;
    const [unitOpen, setUnitOpen] = useState(false);
    const [unitOptions, setUnitOptions] = useState([]);
    const unitLoading = unitOpen && unitOptions.length === 0;
    const [subUnitOpen, setSubUnitOpen] = useState(false);
    const [subUnitOptions, setSubUnitOptions] = useState([]);
    const subUnitLoading = subUnitOpen && subUnitOptions.length === 0;
    const [tribeOpen, setTribeOpen] = useState(false);
    const [tribeOptions, setTribeOptions] = useState([]);
    const tribeLoading = tribeOpen && tribeOptions.length === 0;
    const config = useSelector((state) => state.configuration.config);
    const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
    const [fundingUnitOptions, setFundingUnitOptions] = useState([]);
    const [fundingSourceOptions, setFundingSourceOptions] = useState([]);
    const nifcToken = useSelector((state) => state.configuration.nifcToken);

    const classes = useStyles();
    
    useEffect(() => {
      if(isInteragencyRow)
      {
        setAgencySelection(FieldNames.agencies.owf);
        setRegionSelection("Department of the Interior OWF");
        setUnitSelection("Office of Wildland Fire");
        setFundingUnitTypeSelection(FieldNames.unitTypes.national)
      }
    }, [isInteragencyRow])

    useEffect(() => {
        UserHelperService.getAllFundingUnitTypes().then((resp) => {
            setFundingUnitOptions(resp.map(x => x[FieldNames.units.unitType]));
        }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
        });

        const fundingSources = DetailedFundingSources.fundingSources;
        if (fundingSources && fundingSources.length > 0) {
            const nonContributed = fundingSources.filter(x => x[FieldNames.fundingSources.isContributed] !== `${FieldNames.yesNo.yes}`);
            if (nonContributed && nonContributed.length > 0) {
                setFundingSourceOptions(nonContributed.map(x => x[FieldNames.fundingSources.fundingSource]));
            }
        }

        if (rows && rows.length > 0) {
            const bilRows = rows.filter(x => x[FieldNames.fundingFields.fundingSource] && x[FieldNames.fundingFields.fundingSource].toLowerCase().includes("bil"));
            if (bilRows && bilRows.length > 0) {
                setIsBil(true);
            } else {
                setIsBil(false);
            }
        } else {
            setIsBil(false);
        }
        //setFundingUnitOptions([]);
    }, [config, rootServiceUrl, rows, setIsBil]);

    useEffect(() => {
        setDepartmentSelection((selectedRow && selectedRow[FieldNames.fundingFields.fundingDepartment]) ? selectedRow[FieldNames.fundingFields.fundingDepartment] : FieldNames.defaults.department);
        setDepartmentOptions([FieldNames.defaults.department]);
    }, [selectedRow, setDepartmentSelection]);

    useEffect(() => {
        setAgencySelection((selectedRow && selectedRow[FieldNames.fundingFields.fundingAgency]) ? selectedRow[FieldNames.fundingFields.fundingAgency] : "");
    }, [selectedRow, setAgencySelection]);

    useEffect(() => {
        setRegionSelection((selectedRow && selectedRow[FieldNames.fundingFields.fundingRegion]) ? selectedRow[FieldNames.fundingFields.fundingRegion] : "");
    }, [selectedRow, setRegionSelection]);

    useEffect(() => {
        setUnitSelection((selectedRow && selectedRow[FieldNames.fundingFields.fundingUnit]) ? selectedRow[FieldNames.fundingFields.fundingUnit] : "");
    }, [selectedRow, setUnitSelection]);

    useEffect(() => {
        setSubUnitSelection((selectedRow && selectedRow[FieldNames.fundingFields.fundingSubUnit]) ? selectedRow[FieldNames.fundingFields.fundingSubUnit] : "");
    }, [selectedRow, setSubUnitSelection]);

    useEffect(() => {
        setTribeSelection((selectedRow && selectedRow[FieldNames.fundingFields.fundingTribe]) ? selectedRow[FieldNames.fundingFields.fundingTribe] : "");
    }, [selectedRow, setTribeSelection]);

    useEffect(() => {
        setFundingUnitTypeSelection((selectedRow && selectedRow[FieldNames.fundingFields.fundingUnitType]) ? selectedRow[FieldNames.fundingFields.fundingUnitType] : "");
    }, [selectedRow, setFundingUnitTypeSelection])

    useEffect(() => {
        setCostCenterCodeSelection((selectedRow && selectedRow[FieldNames.fundingFields.costCenter]) ? selectedRow[FieldNames.fundingFields.costCenter] : "");
    }, [selectedRow, setCostCenterCodeSelection]);

    useEffect(() => {
        setFundingUnitIDSelection((selectedRow && selectedRow[FieldNames.fundingFields.fundingUnitID]) ? selectedRow[FieldNames.fundingFields.fundingUnitID] : "");
    }, [selectedRow, setFundingUnitIDSelection]);

    useEffect(() => {
        let active = true;

        if (!departmentLoading) {
            return undefined;
        }

        (async () => {
            if (active) {
                let departments = [];
                userRoleInfo.forEach(item => {
                    if (item.Department && departments && !departments.includes(item.Department)) {
                        departments.push(item.Department);
                    }
                })
                if (departments && departments.length > 0) {
                    setDepartmentOptions(departments);
                } else {
                    setDepartmentOptions([FieldNames.defaults.department]);
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [agencyLoading, departmentSelection, config, rootServiceUrl, userRoleInfo]);

    useEffect(() => {
        let active = true;

        if (!agencyLoading) {
            return undefined;
        }

        (async () => {
            if (active) {
                if (departmentSelection) {
                    let agencies = [];
                    if (userRoleInfo) {
                        userRoleInfo.forEach(item => {
                            if (item.Agency && agencies && !agencies.includes(item.Agency)) {
                                agencies.push(item.Agency);
                            }
                        });
                    }
                    if (agencies && agencies.length > 0) {
                        setAgencyOptions(agencies);
                    } else {
                        UserHelperService.getAgencyByDepartment(departmentSelection).then(response => {
                            if (response && response.length > 0) {
                                setAgencyOptions(response.map(x => x.Agency));
                            } else {
                                setAgencyOptions([""]);
                            }
                        }).catch(error => {
                            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                        });
                    }
                } else {
                    setAgencyOptions([""]);
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [agencyLoading, departmentSelection, config, rootServiceUrl, userRoleInfo]);

    useEffect(() => {
        if (!agencyOpen) {
            setAgencyOptions([]);
        }
    }, [agencyOpen]);

    useEffect(() => {
        let active = true;

        if (!regionLoading) {
            return undefined;
        }

        (async () => {
            if (active) {
                if (agencySelection) {
                    let regions = [];
                    userRoleInfo.forEach(item => {
                        if (item.Region && regions && !regions.includes(item.Region)) {
                            regions.push(item.Region);
                        }
                    })
                    if (regions && regions.length > 0) {
                        setRegionOptions(regions);
                    } else {
                        UserHelperService.getRegionsByAgency(agencySelection).then(response => {
                            if (response && response.length > 0) {
                                setRegionOptions(response.map(x => x.Region));
                            }
                        }).catch(error => {
                            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                        });
                    }
                } else {
                    setRegionOptions([""]);
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [regionLoading, agencySelection, config, rootServiceUrl, userRoleInfo]);

    useEffect(() => {
        if (!regionOpen) {
            setRegionOptions([]);
        }
    }, [regionOpen]);

    useEffect(() => {
        let active = true;

        if (!unitLoading) {
            return undefined;
        }

        (async () => {
            if (active) {
                if (regionSelection) {
                    let units = [];
                    userRoleInfo.forEach(item => {
                        if (item.Region === regionSelection) {
                            if (item.Unit && units && !units.includes(item.Unit)) {
                                units.push(item.Unit);
                            }
                        }
                    })
                    if (units && units.length > 0) {
                        setUnitOptions(units);
                    } else if (agencySelection) {
                        UserHelperService.getUnitsByRegionsAgency([regionSelection], agencySelection).then(response => {
                            if (response && response.length > 0) {
                                setUnitOptions(response.map(x => x.Unit));
                            } else {
                                setUnitOptions([""]);
                            }
                        }).catch(error => {
                            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                        });
                    } else {
                        UserHelperService.getUnitsByRegions([regionSelection]).then((resp) => {
                            if (resp && resp.length > 0) {
                                const returnedUnits = resp.map(x => x.Unit);
                                setUnitOptions(returnedUnits);
                            } else {
                                setUnitOptions([""]);
                            }
                        }).catch(error => {
                            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                        });
                    }
                } else {
                    setUnitOptions([""]);
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [unitLoading, regionSelection, config, rootServiceUrl, agencySelection, userRoleInfo]);

    useEffect(() => {
        if (!unitOpen) {
            setUnitOptions([]);
        }
    }, [unitOpen]);

    useEffect(() => {
        let active = true;

        if (!subUnitLoading) {
            return undefined;
        }

        (async () => {
            if (active) {
                let subUnits = [];
                userRoleInfo.forEach(item => {
                    if (item.Unit === unitSelection) {
                        if (item.SubUnit && subUnits && !subUnits.includes(item.SubUnit)) {
                            subUnits.push(item.SubUnit);
                        }
                    }
                })
                if (subUnits && subUnits.length > 0) {
                    setSubUnitOptions(subUnits);
                } else {
                    if (unitSelection) {
                        UserHelperService.getSubUnitsByUnits([unitSelection]).then((resp) => {
                            if (resp && resp.length > 0) {
                                const returnedSubUnits = resp.map(x => x.SubUnit);
                                setSubUnitOptions(returnedSubUnits);
                            } else {
                                setSubUnitOptions([""]);
                            }
                        }).catch(error => {
                            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                        });
                    } else {
                        setSubUnitOptions([""]);
                    }
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [subUnitLoading, unitSelection, config, rootServiceUrl, userRoleInfo]);

    useEffect(() => {
        if (!subUnitOpen) {
            setSubUnitOptions([]);
        }
    }, [subUnitOpen]);

    useEffect(() => {
        let active = true;

        if (!tribeLoading) {
            return undefined;
        }

        (async () => {
            if (active) {
                let tribes = [];
                userRoleInfo.forEach(item => {
                    //if(item.Unit === parentValue){
                    if (item[FieldNames.userRoles.tribe] && tribes && !tribes.includes(item[FieldNames.userRoles.tribe])) {
                        tribes.push(item[FieldNames.userRoles.tribe]);
                    }
                    //}
                })
                //We need to use the tribe the user has assigned if they have one
                if (tribes && tribes.length > 0) {
                    setSubUnitOptions(tribes);
                } else {
                    //When the user doesn't have a tribe assigned use the other fields to populate this if they exist
                    if (unitSelection) {
                        UserHelperService.getTribesByUnits([unitSelection]).then((resp) => {
                            if (resp && resp.length > 0) {
                                const returnedTribes = resp.map(x => x[FieldNames.units.tribeFullName] ? x[FieldNames.units.tribeFullName] : "");
                                setTribeOptions(returnedTribes);
                            } else {
                                setTribeOptions([""]);
                            }
                        }).catch(error => {
                            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                        });
                    } else {
                        setTribeOptions([""]);
                    }
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [tribeLoading, unitSelection, regionSelection, config, rootServiceUrl, userRoleInfo]);

    useEffect(() => {
        if (!tribeOpen) {
            setTribeOptions([]);
        }
    }, [tribeOpen]);

    useEffect(() => {
        validateFields();
    }, [departmentSelection, agencySelection, regionSelection,unitSelection, subUnitSelection, tribeSelection, fundingUnitTypeSelection, selectedRow])

    const isMatching = (field, value) => {
        if (field && field.toLowerCase().includes(value)) {
            return true;
        } else {
            return false;
        }
    }

    const validateFields = () => {
        let isValid = true;
        if (requiredFields.includes(FieldNames.featureFields.fundingDepartment)){
            isValid = (departmentSelection  && departmentSelection !== "") === true;
        }
        if (requiredFields.includes(FieldNames.featureFields.fundingAgency)) {
            isValid = isValid && (agencySelection && agencySelection !== "");
        }
        if( requiredFields.includes(FieldNames.featureFields.fundingRegion)) {
            isValid = isValid && (regionSelection && regionSelection !== "");
        }
        if (requiredFields.includes(FieldNames.featureFields.fundingUnit)){
            isValid = isValid && (unitSelection && unitSelection !== "");
        }

        if(requiredFields.includes(FieldNames.featureFields.fundingSubUnit) ){
            isValid = isValid && (subUnitSelection && subUnitSelection !== "");
        }

        if( requiredFields.includes(FieldNames.featureFields.fundingTribe) ){
            isValid = isValid && (tribeSelection && tribeSelection !== "");
        }

        if(requiredFields.includes(FieldNames.featureFields.fundingUnitType)){
            isValid = isValid && (fundingUnitTypeSelection && fundingUnitTypeSelection !== "");
        }
        setFundingValid(isValid);
    };

    return (
        <Fragment>
            <FormControl variant="standard" className={classes.formControl}>
                <ThemeProvider theme={theme}>
                    <Grid item size={12} container direction="row" className="grid-item"
                           style={{alignItems: 'center'}}>
                        <Grid item size={6}>
              <span>
                <label>Funding Department</label>
                  {requiredFields.includes(FieldNames.featureFields.fundingDepartment) &&
                      <span className="required">*</span>
                  }
                  <DetailsPopover mode={"edit"} field={FieldNames.featureFields.fundingDepartment}
                                  isTreatment={isTreatment} categoryValue={null}
                                  isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no} isActuals={isActual}
                                  agencyValue={agencySelection} isBulkApproval={false}/>
              </span>
                        </Grid>
                        <Grid item size={6}>
                            <Autocomplete
                                id="departmentDemo"
                                // value={departmentSelection}
                                // disabled={(agencySelection) ? true : false }
                                value={FieldNames.defaults.department}
                                disabled={true}
                                onChange={(event, newValue) => {
                                    setDepartmentSelection(newValue);
                                    setAgencySelection("");
                                    setRegionSelection("");
                                    setUnitSelection("");
                                    setSubUnitSelection("");
                                    setTribeSelection("");
                                    setFundingUnitTypeSelection("");
                                }}
                                isOptionEqualToValue={(option, value) => option === value}
                                getOptionLabel={(option) => {
                                    return option
                                }}
                                options={departmentOptions}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {params?.InputProps?.endAdornment}
                                                    </React.Fragment>
                                                )
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </ThemeProvider>
            </FormControl>
            <FormControl variant="standard" className={classes.formControl}>
                <ThemeProvider theme={theme}>
                    <Grid item size={12} container direction="row" className="grid-item"
                           style={{alignItems: 'center'}}>
                        <Grid item size={6}>
              <span>
                <label>Funding Agency</label>
                  {requiredFields.includes(FieldNames.featureFields.fundingAgency) &&
                      <span className="required">*</span>
                  }
                  <DetailsPopover mode={"edit"} field={FieldNames.featureFields.fundingAgency} isTreatment={isTreatment}
                                  categoryValue={null} isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no}
                                  isActuals={isActual} agencyValue={agencySelection} isBulkApproval={false}/>
              </span>
                        </Grid>
                        <Grid item size={6}>
                            <Autocomplete
                                id="agencyDemo"
                                value={agencySelection}
                                disabled={isInteragencyRow}
                                onChange={(event, newValue) => {
                                    setAgencySelection(newValue);
                                    setRegionSelection("");
                                    setUnitSelection("");
                                    setSubUnitSelection("");
                                    setTribeSelection("");
                                    setFundingUnitTypeSelection("");
                                }}
                                onOpen={() => {
                                    setAgencyOpen(true);
                                }}
                                onClose={() => {
                                    setAgencyOpen(false);
                                }}
                                isOptionEqualToValue={(option, value) => option === value}
                                getOptionLabel={(option) => {
                                    return option
                                }}
                                options={agencyOptions}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {params?.InputProps?.endAdornment}
                                                    </React.Fragment>
                                                )
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </ThemeProvider>
            </FormControl>
            <FormControl variant="standard" className={classes.formControl}>
                <ThemeProvider theme={theme}>
                    <Grid item size={12} container direction="row" className="grid-item"
                           style={{alignItems: 'center'}}>
                        <Grid item size={6}>
              <span>
                <label>Funding Region</label>
                  {requiredFields.includes(FieldNames.featureFields.fundingRegion) &&
                      <span className="required">*</span>
                  }
                  <DetailsPopover mode={"edit"} field={FieldNames.featureFields.fundingRegion} isTreatment={isTreatment}
                                  categoryValue={null} isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no}
                                  isActuals={isActual} agencyValue={agencySelection} isBulkApproval={false}/>
              </span>
                        </Grid>
                        <Grid item size={6}>
                            <Autocomplete
                                id="regionMulti"
                                open={regionOpen}
                                value={regionSelection}
                                disabled={isInteragencyRow}
                                onChange={(event, newValue) => {
                                    setRegionSelection(newValue);
                                    setUnitSelection("");
                                    setSubUnitSelection("");
                                    setTribeSelection("");
                                    setFundingUnitTypeSelection("");
                                }}
                                onOpen={() => {
                                    setRegionOpen(true);
                                }}
                                onClose={() => {
                                    setRegionOpen(false);
                                }}
                                isOptionEqualToValue={(option, value) => {
                                    return option === value
                                }}
                                getOptionLabel={(option) => {
                                    return option
                                }}
                                options={regionOptions}
                                loading={regionLoading}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip
                                            label={option[FieldNames.units.region]}
                                            {...getTagProps({index})}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {regionLoading ?
                                                            <CircularProgress color="inherit" size={20}/> : null}
                                                        {params?.InputProps?.endAdornment}
                                                    </React.Fragment>
                                                )
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </ThemeProvider>
            </FormControl>
            <FormControl variant="standard" className={classes.formControl}>
                <ThemeProvider theme={theme}>
                    <Grid item size={12} container direction="row" className="grid-item"
                           style={{alignItems: 'center'}}>
                        <Grid item size={6}>
              <span>
                <label>Funding Unit</label>
                  {requiredFields.includes(FieldNames.featureFields.fundingUnit) &&
                      <span className="required">*</span>
                  }
                  <DetailsPopover mode={"edit"} field={FieldNames.featureFields.fundingUnit} isTreatment={isTreatment}
                                  categoryValue={null} isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no}
                                  isActuals={isActual} agencyValue={agencySelection} isBulkApproval={false}/>
              </span>
                        </Grid>
                        <Grid item size={6}>
                            <Autocomplete
                                id="unitMulti"
                                open={unitOpen}
                                value={unitSelection}
                                disabled={isInteragencyRow}
                                onChange={(event, newValue) => {
                                    setUnitSelection(newValue);
                                    setSubUnitSelection("");
                                    setTribeSelection("");
                                    //When changing Unit the subUnit and tribe values should be cleared
                                    UserHelperService.getUnitEntryByAllFields(departmentSelection, agencySelection, regionSelection, newValue, "", "").then(resp => {
                                        if (resp && resp.length > 0) {
                                            setFundingUnitTypeSelection(resp[0][FieldNames.units.unitType]);
                                            setCostCenterCodeSelection(resp[0][FieldNames.units.costCenterCode]);
                                            setFundingUnitIDSelection(resp[0][FieldNames.units.unitID]);
                                        }
                                    }).catch(error => {
                                        ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                                    });
                                }}
                                onOpen={() => {
                                    setUnitOpen(true);
                                }}
                                onClose={() => {
                                    setUnitOpen(false);
                                }}
                                isOptionEqualToValue={(option, value) => {
                                    return option === value
                                }}
                                getOptionLabel={(option) => {
                                    return option
                                }}
                                options={unitOptions}
                                getOptionDisabled={(option) => {
                                    if (unitSelection && unitSelection.includes(option)) {
                                        return true;
                                    } else {
                                        return false;
                                    }
                                }}
                                loading={unitLoading}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip
                                            label={option[FieldNames.units.unit]}
                                            {...getTagProps({index})}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {unitLoading ?
                                                            <CircularProgress color="inherit" size={20}/> : null}
                                                        {params?.InputProps?.endAdornment}
                                                    </React.Fragment>
                                                )
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </ThemeProvider>
            </FormControl>
            {(agencySelection && isMatching(agencySelection, 'blm')) &&
                <FormControl variant="standard" className={classes.formControl}>
                    <ThemeProvider theme={theme}>
                        <Grid item size={12} container direction="row" className="grid-item"
                               style={{alignItems: 'center'}}>
                            <Grid item size={6}>
                <span>
                  <label>Funding SubUnit</label>
                    {requiredFields.includes(FieldNames.featureFields.fundingSubUnit) &&
                        <span className="required">*</span>
                    }
                    <DetailsPopover mode={"edit"} field={FieldNames.featureFields.fundingSubUnit}
                                    isTreatment={isTreatment} categoryValue={null}
                                    isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no} isActuals={isActual}
                                    agencyValue={agencySelection} isBulkApproval={false}/>
                </span>
                            </Grid>
                            <Grid item size={6}>
                                <Autocomplete
                                    id="subUnitMulti"
                                    open={subUnitOpen}
                                    value={subUnitSelection}
                                    disableClearable
                                    onChange={(event, newValue) => {
                                        setSubUnitSelection(newValue);
                                        //Tribe will always be empty if we are querying with subUnit
                                        UserHelperService.getUnitEntryByAllFields(departmentSelection, agencySelection, regionSelection, unitSelection, newValue, "").then(resp => {
                                            if (resp && resp.length > 0) {
                                                setFundingUnitTypeSelection(resp[0][FieldNames.units.unitType]);
                                                setCostCenterCodeSelection(resp[0][FieldNames.units.costCenterCode]);
                                                setFundingUnitIDSelection(resp[0][FieldNames.units.unitID]);
                                            }
                                        }).catch(error => {
                                            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                                        });
                                    }}
                                    onOpen={() => {
                                        setSubUnitOpen(true);
                                    }}
                                    onClose={() => {
                                        setSubUnitOpen(false);
                                    }}
                                    isOptionEqualToValue={(option, value) => {
                                        return option === value
                                    }}
                                    getOptionLabel={(option) => {
                                        return option
                                    }}
                                    options={subUnitOptions}
                                    getOptionDisabled={(option) => {
                                        if (subUnitSelection && subUnitSelection.includes(option)) {
                                            return true;
                                        } else {
                                            return false;
                                        }
                                    }}
                                    loading={subUnitLoading}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            slotProps={{
                                                input: {
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {subUnitLoading ?
                                                                <CircularProgress color="inherit" size={20}/> : null}
                                                            {params?.InputProps?.endAdornment}
                                                        </React.Fragment>
                                                    )
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </ThemeProvider>
                </FormControl>
            }
            {(agencySelection && isMatching(agencySelection, "bia")) &&
                <FormControl variant="standard" className={classes.formControl}>
                    <ThemeProvider theme={theme}>
                        <Grid item size={12} container direction="row" className="grid-item"
                               style={{alignItems: 'center'}}>
                            <Grid item size={6}>
              <span>
                <label>Funding Tribe</label>
                  {requiredFields.includes(FieldNames.featureFields.fundingTribe) &&
                      <span className="required">*</span>
                  }
                  <DetailsPopover mode={"edit"} field={FieldNames.featureFields.fundingTribe} isTreatment={isTreatment}
                                  categoryValue={null} isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no}
                                  isActuals={isActual} agencyValue={agencySelection} isBulkApproval={false}/>
              </span>
                            </Grid>
                            <Grid item size={6}>
                                <Autocomplete
                                    id="tribeSingular"
                                    open={tribeOpen}
                                    value={tribeSelection}
                                    onChange={(event, newValue) => {
                                        setTribeSelection(newValue);
                                        let region = regionSelection;
                                        let unit = unitSelection;
                                        if (newValue) {
                                            UserHelperService.getRegionUnitWithTribe(newValue).then(resp => {
                                                if (resp && resp.length > 0) {
                                                    // We never get inside of an else. If there is a region/unit selected then the tribe is filtered by that option already
                                                    // Only the tribes that fall under the selected region are available for selection
                                                    if (!regionSelection || (regionSelection && regionSelection.length === 0)) {
                                                        setRegionSelection(resp[0][FieldNames.units.region]);
                                                        region = resp[0][FieldNames.units.region];
                                                        //Since the region selection is empty set it to the newly selected one
                                                    }

                                                    if (!unitSelection || (unitSelection && unitSelection.length === 0)) {
                                                        setUnitSelection(resp[0][FieldNames.units.unit]);
                                                        unit = resp[0][FieldNames.units.unit];
                                                        //Since the unit selection is empty set it to the newly selected one
                                                    }
                                                }
                                            }).catch(error => {
                                                ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                                            });
                                        }
                                        //SubUnit will always be empty if we are querying with tribe
                                        UserHelperService.getUnitEntryByAllFields(departmentSelection, agencySelection, region, unit, "", newValue).then(resp => {
                                            if (resp && resp.length > 0) {
                                                setFundingUnitTypeSelection(resp[0][FieldNames.units.unitType]);
                                                setCostCenterCodeSelection(resp[0][FieldNames.units.costCenterCode]);
                                                setFundingUnitIDSelection(resp[0][FieldNames.units.unitID]);
                                            }
                                        }).catch(error => {
                                            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                                        });
                                    }}
                                    onOpen={() => {
                                        setTribeOpen(true);
                                    }}
                                    onClose={() => {
                                        setTribeOpen(false);
                                    }}
                                    isOptionEqualToValue={(option, value) => {
                                        if (option && value) {
                                            return option === value;
                                        } else {
                                            return false;
                                        }
                                    }}
                                    getOptionLabel={(option) => {
                                        return (option) ? option : '';
                                    }}
                                    options={tribeOptions}
                                    getOptionDisabled={(option) => {
                                        if (tribeSelection === option) {
                                            return true;
                                        } else {
                                            return false;
                                        }
                                    }}
                                    loading={tribeLoading}
                                    renderTags={(tagValue, getTagProps) =>
                                        tagValue.map((option, index) => (
                                            <Chip
                                                label={option[FieldNames.units.tribeFullName]}
                                                {...getTagProps({index})}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            slotProps={{
                                                input: {
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {tribeLoading ?
                                                                <CircularProgress color="inherit" size={20}/> : null}
                                                            {params?.InputProps?.endAdornment}
                                                        </React.Fragment>
                                                    )
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </ThemeProvider>
                </FormControl>
            }
            <FormControl variant="standard" className={classes.formControl}>
                <ThemeProvider theme={theme}>
                    <Grid item size={12} container direction="row" className="grid-item"
                           style={{alignItems: 'center'}}>
                        <Grid item size={6}>
              <span>
                <label>Funding Unit Type</label>
                  {requiredFields.includes(FieldNames.featureFields.fundingUnitType) &&
                      <span className="required">*</span>
                  }
                  <DetailsPopover mode={"edit"} field={FieldNames.featureFields.fundingUnitType}
                                  isTreatment={isTreatment} categoryValue={null}
                                  isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no} isActuals={isActual}
                                  agencyValue={agencySelection} isBulkApproval={false}/>
              </span>
                        </Grid>
                        <Grid item size={6}>
                            <Autocomplete
                                id="unitMulti"
                                value={fundingUnitTypeSelection}
                                disabled={true}
                                options={fundingUnitOptions}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {unitLoading ?
                                                            <CircularProgress color="inherit" size={20}/> : null}
                                                        {params?.InputProps?.endAdornment}
                                                    </React.Fragment>
                                                )
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </ThemeProvider>
            </FormControl>
        </Fragment>
    );
}